import React, { useState, useEffect } from 'react';
import AdsCard from '../AdsCard';
import snapchat from '../../../../assests/snapchat.svg';
import { toast } from 'react-toastify';
import {
  fetchCredentials,
  fetchAuthCodeFromSnapchat,
  getOrganizationList,
} from '../../../../services/ApiService';
import {
  clearOAuthCallbackParams,
  clearSnapchatOAuthState,
  getOAuthRedirectUri,
} from '../../../../utils/oauthRedirect';
import { getIntegrationErrorMessage } from '../../../../utils/oauthErrors';
import { SnapchatAdsOrganisationsTable } from './SnapchatAdsOrganisationsTable';
import { SnapchatAdsAccountsTable } from './SnapchatAdsAccountsTable';
import { CommonModal } from '../../../CommonModal';
import { useNavigate } from 'react-router-dom';

const snapchatOAuthDevLog = (...args) => {
  if (process.env.NODE_ENV === 'development') {
    console.log('[Snapchat OAuth Dev]', ...args);
  }
};

const SnapchatAdsIntegration = ({
  isAccountLinked,
  fetchAdsAccountStatus,
  accountDetails,
}) => {
  const navigate = useNavigate();
  const [connecting, setConnecting] = useState(false);
  const [organisations, setOrganisations] = useState([]);
  const [adAccounts, setAdAccounts] = useState([]);
  const [snapchatUserInfo, setSnapchatUserInfo] = useState({
    organization_id: '',
    ad_accounts_id: '',
  });
  const [showOrganisations, setShowOrganisations] = useState(false);
  const [showAdAccountsModal, setShowAdAccountsModal] = useState(false);

  const finishOAuthCallback = () => {
    clearSnapchatOAuthState();
    clearOAuthCallbackParams(navigate);
  };

  const handleConnect = async () => {
    setConnecting(true);
    localStorage.setItem('selected_platform_to_connect', 'snapchat');
    localStorage.removeItem('snapchat_code');

    const redirectUri = getOAuthRedirectUri();
    snapchatOAuthDevLog('Connect clicked', { redirect_uri: redirectUri });

    if (!redirectUri) {
      setConnecting(false);
      toast.error('Snapchat redirect URL is not configured.');
      return;
    }

    const credentials = await fetchCredentials();

    if (credentials?.snapchat?.snapchat_ads_client_id) {
      fetchAuthCodeFromSnapchat(credentials.snapchat.snapchat_ads_client_id, redirectUri);
      return;
    }

    setConnecting(false);
    toast.error('Error connecting to Snapchat');
  };

  const connectToAccount = async (code) => {
    setConnecting(true);
    snapchatOAuthDevLog('Processing OAuth callback', {
      code_prefix: `${code?.slice(0, 12)}...`,
      redirect_uri: getOAuthRedirectUri(),
    });

    try {
      const result = await getOrganizationList(code);

      if (result?.success) {
        const orgs = Array.isArray(result?.data?.organisations) ? result.data.organisations : [];

        if (orgs.length > 0) {
          setOrganisations(orgs);
          setShowOrganisations(true);
          clearOAuthCallbackParams(navigate);
          return;
        }

        toast.error(
          'No Snapchat organizations found for this account. Please verify your Snapchat Ads access.',
        );
        finishOAuthCallback();
        return;
      }

      toast.error(getIntegrationErrorMessage(result, 'Failed to connect to Snapchat.'));
      finishOAuthCallback();
    } catch (e) {
      console.error('[Snapchat OAuth Dev] Connect failed', e);
      toast.error(getIntegrationErrorMessage(e, 'Failed to connect to Snapchat.'));
      finishOAuthCallback();
    } finally {
      setConnecting(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const error = params.get('error');
    const errorDescription = params.get('error_description');
    const selectedPlatform = localStorage.getItem('selected_platform_to_connect');

    snapchatOAuthDevLog('OAuth callback check', {
      has_code: Boolean(code),
      has_error: Boolean(error),
      selected_platform_to_connect: selectedPlatform,
    });

    if (selectedPlatform !== 'snapchat') {
      return;
    }

    if (error) {
      toast.error(
        errorDescription ||
          `Snapchat authorization failed. Add this redirect URL in Snapchat Ads Manager: ${getOAuthRedirectUri()}`,
      );
      finishOAuthCallback();
      setConnecting(false);
      return;
    }

    if (code) {
      connectToAccount(code);
    }
  }, []);

  return (
    <>
      <AdsCard
        handleConnect={handleConnect}
        isAccountLinked={isAccountLinked || false}
        clientData={{}}
        platform="Snapchat Ads"
        platform_icon={snapchat}
        description="Snapchat Ads lets businesses reach users on Snapchat with photo, video, and story-based advertising formats."
        connecting={connecting}
        accountDetails={accountDetails}
        social_media_type="paid_social"
        fetchAdsAccountStatus={fetchAdsAccountStatus}
      />

      <CommonModal
        show={showOrganisations}
        setShow={setShowOrganisations}
        title={
          <div className="d-flex flex-column gap-1">
            <span className="fs-4 fw-bold">Organizations</span>
            <small className="fs-8">Select an organization to proceed with integration.</small>
          </div>
        }
        body={
          <SnapchatAdsOrganisationsTable
            organisations={organisations}
            setAdAccounts={setAdAccounts}
            setShowAdAccountsModal={setShowAdAccountsModal}
            setSnapchatUserInfo={setSnapchatUserInfo}
          />
        }
      />

      <CommonModal
        show={showAdAccountsModal}
        setShow={setShowAdAccountsModal}
        title={
          <div className="d-flex flex-column gap-1">
            <span className="fs-4 fw-bold">Ad Accounts</span>
            <small className="fs-8">Select an Ad Account to proceed with integration.</small>
          </div>
        }
        body={
          <SnapchatAdsAccountsTable
            adAccounts={adAccounts}
            setSnapchatUserInfo={setSnapchatUserInfo}
            setShowAdAccountsModal={setShowAdAccountsModal}
            setShowOrganisations={setShowOrganisations}
            snapchatUserInfo={snapchatUserInfo}
            fetchAdsAccountStatus={fetchAdsAccountStatus}
            onLinked={finishOAuthCallback}
          />
        }
      />
    </>
  );
};

export default SnapchatAdsIntegration;
